import axios from 'axios';
import {
  joinUrl,
  MAX_RETRIES,
  PARALLEL_CHUNK_SIZE,
  REQUEST_TIMEOUT_MS,
  RETRY_BASE_DELAY_MS,
} from './apiConfig';

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function shouldRetry(status, err) {
  if (err && err.code === 'ECONNABORTED') return true;
  if (!status || status === 0) return true;
  if (status >= 500) return true;
  return false;
}

function evaluatePassFail(status, auth) {
  if (status === 0) {
    return { pass: false, label: 'FAIL' };
  }
  if (status >= 500) {
    return { pass: false, label: 'FAIL' };
  }
  if (status >= 200 && status < 500) {
    if (auth === 'protected' && status === 401) {
      return { pass: false, label: 'FAIL' };
    }
    if (auth === 'public' && status === 401) {
      return { pass: false, label: 'FAIL' };
    }
    return { pass: true, label: 'PASS' };
  }
  return { pass: false, label: 'FAIL' };
}

function buildBodyForPost() {
  const p = new URLSearchParams();
  p.append('_health_probe', '1');
  return p;
}

export async function probeRoute(route, apiBase, token, signal) {
  const url = joinUrl(apiBase, route.path);
  const auth = route.auth || 'protected';
  const usedAuthHeader = auth === 'protected' && !!token;
  const headers = {};
  if (usedAuthHeader) {
    headers.Authorization = `Bearer ${token}`;
  }
  const method = (route.method || 'GET').toUpperCase();
  let attempt = 0;
  let lastStatus = 0;
  let lastErrMsg = '';
  let durationMs = 0;
  while (attempt <= MAX_RETRIES) {
    const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
    try {
      let res;
      if (method === 'GET' || method === 'HEAD') {
        res = await axios.request({
          url,
          method,
          headers,
          timeout: REQUEST_TIMEOUT_MS,
          signal,
          validateStatus: () => true,
        });
      } else {
        res = await axios.request({
          url,
          method,
          headers: {
            ...headers,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          data: buildBodyForPost(),
          timeout: REQUEST_TIMEOUT_MS,
          signal,
          validateStatus: () => true,
        });
      }
      durationMs = Math.round(
        (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0
      );
      lastStatus = res.status;
      const ev = evaluatePassFail(lastStatus, auth);
      if (!ev.pass && shouldRetry(lastStatus, null) && attempt < MAX_RETRIES) {
        attempt += 1;
        await sleep(RETRY_BASE_DELAY_MS * attempt);
        continue;
      }
      return {
        route,
        url,
        statusCode: lastStatus,
        durationMs,
        pass: ev.pass,
        passLabel: ev.label,
        errorMessage: ev.pass ? '' : `HTTP ${lastStatus}`,
        authMode: auth,
      };
    } catch (e) {
      durationMs = Math.round(
        (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0
      );
      lastStatus = e.response?.status || 0;
      lastErrMsg =
        e.response?.data?.message ||
        e.message ||
        'request_error';
      if (shouldRetry(lastStatus, e) && attempt < MAX_RETRIES) {
        attempt += 1;
        await sleep(RETRY_BASE_DELAY_MS * attempt);
        continue;
      }
      return {
        route,
        url,
        statusCode: lastStatus || 0,
        durationMs,
        pass: false,
        passLabel: 'FAIL',
        errorMessage: lastErrMsg,
        authMode: auth,
      };
    }
  }
  return {
    route,
    url,
    statusCode: lastStatus || 0,
    durationMs,
    pass: false,
    passLabel: 'FAIL',
    errorMessage: lastErrMsg || 'probe_exhausted',
    authMode: auth,
  };
}

export async function runProbeSuite(
  routes,
  apiBase,
  token,
  signal,
  onChunkDone,
  options = {}
) {
  const sequential = Boolean(options.sequential);
  const active = routes.filter((r) => r.auth !== 'skip');
  const skipped = routes.filter((r) => r.auth === 'skip').map((r) => ({
    route: r,
    url: joinUrl(apiBase, r.path),
    statusCode: null,
    durationMs: null,
    pass: null,
    passLabel: 'SKIP',
    errorMessage: '',
    authMode: 'skip',
  }));
  const out = [...skipped];
  const totalCount = active.length + skipped.length;
  if (sequential) {
    for (let i = 0; i < active.length; i += 1) {
      const row = await probeRoute(active[i], apiBase, token, signal);
      out.push(row);
      if (typeof onChunkDone === 'function') {
        onChunkDone(out.length, totalCount);
      }
    }
    return out;
  }
  for (let i = 0; i < active.length; i += PARALLEL_CHUNK_SIZE) {
    const chunk = active.slice(i, i + PARALLEL_CHUNK_SIZE);
    const part = await Promise.all(
      chunk.map((route) => probeRoute(route, apiBase, token, signal))
    );
    out.push(...part);
    if (typeof onChunkDone === 'function') {
      onChunkDone(out.length, totalCount);
    }
  }
  return out;
}
